import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Loader2, Sprout, Droplets, Layers, Beaker,
  MapPin, Satellite, AlertTriangle, CheckCircle2, Sparkles, Cloud,
} from "lucide-react";
import { toast } from "sonner";
import { TopBar, BottomNav } from "../components/Nav";
import { API } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";

// ESRI World Imagery is free, no key required. Uses XYZ tiles.
const TILE_URL = (z, x, y) =>
  `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`;

// Convert lat/lon to XYZ tile coordinates at zoom z
function lonLatToTile(lon, lat, z) {
  const x = Math.floor(((lon + 180) / 360) * 2 ** z);
  const latRad = (lat * Math.PI) / 180;
  const y = Math.floor(
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * 2 ** z
  );
  return { x, y };
}

export default function SoilReportPage() {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const fieldId = params.get("field");

  const [coords, setCoords] = useState(null); // {lat, lon}
  const [crop, setCrop] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [locating, setLocating] = useState(false);
  const fetchedRef = useRef(false);

  // Auto-prompt geolocation once on mount
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    requestLocation();
  }, []);

  const requestLocation = () => {
    if (!navigator.geolocation) {
      toast.error(t("soil.geo.unsupported"));
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: +pos.coords.latitude.toFixed(5), lon: +pos.coords.longitude.toFixed(5) });
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        toast.error(err.code === 1 ? t("soil.geo.denied") : t("soil.geo.failed"));
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  };

  const generate = async () => {
    if (!coords) {
      toast.error(t("soil.needLocation"));
      return;
    }
    setBusy(true);
    setReport(null);
    try {
      const r = await axios.post(`${API}/soil-report`, {
        lat: coords.lat,
        lon: coords.lon,
        language: lang,
        field_id: fieldId || undefined,
        crop_name: crop || undefined,
      });
      setReport(r.data);
      toast.success(t("soil.done"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("soil.failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-24 md:pb-10">
      <TopBar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        <Link to={fieldId ? `/fields/${fieldId}` : "/dashboard"} data-testid="soil-back" className="inline-flex items-center gap-1 text-[#1B4332] font-semibold hover:underline">
          <ArrowLeft className="w-4 h-4" /> {t("common.back")}
        </Link>

        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#1B4332] flex items-center justify-center shrink-0">
            <Satellite className="w-5 h-5 text-[#D4A373]" />
          </div>
          <div>
            <h1 className="cd-heading text-3xl font-extrabold leading-tight">{t("soil.title")}</h1>
            <p className="text-sm text-[#52796F] mt-1">{t("soil.subtitle")}</p>
          </div>
        </div>

        {/* Location + crop input */}
        <div className="bg-white rounded-2xl border border-[#E5E0D8] p-5 space-y-4">
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-[#1B4332] mt-1 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-sm">{t("soil.location")}</div>
              {coords ? (
                <div className="text-sm text-[#2D3A35] mt-0.5" data-testid="soil-coords">
                  {coords.lat.toFixed(4)}, {coords.lon.toFixed(4)}
                </div>
              ) : (
                <div className="text-sm text-[#52796F] mt-0.5">{t("soil.location.none")}</div>
              )}
            </div>
            <button
              data-testid="soil-relocate"
              onClick={requestLocation}
              disabled={locating}
              className="text-xs bg-[#F4F1EA] hover:bg-[#E5E0D8] text-[#1B4332] font-semibold px-3 py-2 rounded-full disabled:opacity-50"
            >
              {locating ? <Loader2 className="w-3 h-3 animate-spin" /> : t("soil.relocate")}
            </button>
          </div>

          {/* Satellite preview (1 tile centered on the user) */}
          {coords && <SatelliteThumb lat={coords.lat} lon={coords.lon} />}

          <div>
            <label className="text-sm font-semibold text-[#1C1C1C]">{t("soil.crop")} {t("common.optional")}</label>
            <input
              data-testid="soil-crop-input"
              value={crop}
              onChange={(e) => setCrop(e.target.value)}
              placeholder={t("soil.crop.placeholder")}
              className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-xl px-4 py-3 focus:outline-none focus:border-[#1B4332]"
            />
          </div>

          <button
            data-testid="soil-generate-btn"
            onClick={generate}
            disabled={!coords || busy}
            className="w-full bg-[#1B4332] text-white hover:bg-[#2D6A4F] py-4 rounded-full font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? (
              <><Loader2 className="w-5 h-5 animate-spin" /> {t("soil.generating")}</>
            ) : (
              <><Sparkles className="w-5 h-5" /> {t("soil.generate")}</>
            )}
          </button>
        </div>

        {report && <ReportCard report={report} t={t} />}
      </main>
      <BottomNav />
    </div>
  );
}

const SatelliteThumb = ({ lat, lon }) => {
  // Show a 3x3 grid of ESRI satellite tiles centered on lat/lon at zoom 15
  const z = 15;
  const center = lonLatToTile(lon, lat, z);
  const tiles = [];
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      tiles.push({
        x: center.x + dx,
        y: center.y + dy,
        key: `${dx}_${dy}`,
      });
    }
  }
  return (
    <div data-testid="soil-satellite" className="relative rounded-xl overflow-hidden border border-[#E5E0D8] bg-[#F4F1EA] aspect-[3/2]">
      <div className="grid grid-cols-3 grid-rows-3 w-full h-full">
        {tiles.map((t) => (
          <img
            key={t.key}
            src={TILE_URL(z, t.x, t.y)}
            alt="satellite"
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ))}
      </div>
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-6 h-6 rounded-full border-2 border-[#D4A373] bg-[#D4A373]/40 ring-4 ring-white/40" />
      </div>
      <div className="absolute bottom-1.5 right-2 text-[9px] text-white/80 bg-black/30 backdrop-blur-sm px-1.5 py-0.5 rounded">
        © Esri World Imagery
      </div>
    </div>
  );
};

const ReportCard = ({ report, t }) => {
  const p = report.properties || {};
  const showFallbackBadge = report.data_source === "regional_estimate" || report.data_source === "mixed";

  return (
    <div data-testid="soil-report" className="space-y-4">
      {showFallbackBadge && (
        <div data-testid="soil-fallback-badge" className="bg-[#E9C46A]/20 border border-[#E9C46A]/40 rounded-2xl p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-[#8B5A00] shrink-0 mt-0.5" />
          <div className="text-xs text-[#2D3A35] leading-snug">
            <span className="font-bold">{report.data_source === "regional_estimate" ? t("soil.regional.title") : t("soil.partial.title")}</span>
            <span> — {t("soil.regional.body")}</span>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-[#E5E0D8] p-5">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle2 className="w-5 h-5 text-[#2B9348]" />
          <div className="font-bold cd-heading text-lg">{t("soil.summary")}</div>
        </div>
        <p className="text-sm text-[#2D3A35] leading-relaxed">{report.recommendation}</p>
      </div>

      {/* Soil composition + texture */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Stat icon={<Beaker className="w-4 h-4" />} label={t("soil.ph")} value={p.ph != null ? p.ph.toFixed(1) : "—"} hint={phHint(p.ph, t)} testid="soil-ph" />
        <Stat icon={<Layers className="w-4 h-4" />} label={t("soil.texture")} value={report.soil_texture || "—"} testid="soil-texture-card" />
        <Stat icon={<Sprout className="w-4 h-4" />} label={t("soil.oc")} value={p.organic_carbon != null ? `${p.organic_carbon} g/kg` : "—"} testid="soil-oc" />
        <Stat icon={<Sparkles className="w-4 h-4" />} label={t("soil.nitrogen")} value={p.nitrogen != null ? `${p.nitrogen} cg/kg` : "—"} testid="soil-n" />
      </div>

      {/* Sand/Silt/Clay donut */}
      {p.sand_pct != null && p.silt_pct != null && p.clay_pct != null && (
        <div data-testid="soil-composition" className="bg-white rounded-2xl border border-[#E5E0D8] p-5">
          <div className="font-bold cd-heading text-base mb-3">{t("soil.composition")}</div>
          <div className="flex items-center gap-4">
            <CompositionBar label={t("soil.sand")} pct={p.sand_pct} color="#D4A373" testid="soil-sand" />
            <CompositionBar label={t("soil.silt")} pct={p.silt_pct} color="#52796F" testid="soil-silt" />
            <CompositionBar label={t("soil.clay")} pct={p.clay_pct} color="#1B4332" testid="soil-clay" />
          </div>
        </div>
      )}

      {/* Soil moisture */}
      {(report.soil_moisture_0_7cm != null || report.soil_moisture_7_28cm != null) && (
        <div data-testid="soil-moisture" className="bg-white rounded-2xl border border-[#E5E0D8] p-5">
          <div className="font-bold cd-heading text-base mb-3 flex items-center gap-1.5">
            <Cloud className="w-4 h-4 text-[#52796F]" /> {t("soil.moisture.title")}
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Moisture label={t("soil.moisture.surface")} value={report.soil_moisture_0_7cm} />
            <Moisture label={t("soil.moisture.deep")} value={report.soil_moisture_7_28cm} />
          </div>
        </div>
      )}

      {report.actions?.length > 0 && (
        <List icon={<CheckCircle2 className="w-4 h-4 text-[#1B4332]" />} title={t("soil.actions")} items={report.actions} testid="soil-actions" />
      )}
      {report.fertilizer_advice?.length > 0 && (
        <List icon={<Sprout className="w-4 h-4 text-[#2B9348]" />} title={t("soil.fertilizer")} items={report.fertilizer_advice} testid="soil-fertilizer" />
      )}
      {report.irrigation_advice?.length > 0 && (
        <List icon={<Droplets className="w-4 h-4 text-[#52796F]" />} title={t("soil.irrigation")} items={report.irrigation_advice} testid="soil-irrigation" />
      )}
    </div>
  );
};

const Stat = ({ icon, label, value, hint, testid }) => (
  <div data-testid={testid} className="bg-white rounded-2xl border border-[#E5E0D8] p-4">
    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-[#52796F] font-semibold">
      {icon} {label}
    </div>
    <div className="font-bold text-[#1C1C1C] mt-1.5 text-base">{value}</div>
    {hint && <div className="text-[11px] text-[#52796F] mt-0.5">{hint}</div>}
  </div>
);

const CompositionBar = ({ label, pct, color, testid }) => (
  <div data-testid={testid} className="flex-1 text-center">
    <div className="relative w-full h-20 rounded-lg overflow-hidden bg-[#F4F1EA]">
      <div
        className="absolute bottom-0 left-0 right-0 transition-all"
        style={{ height: `${Math.max(0, Math.min(100, pct))}%`, background: color }}
      />
    </div>
    <div className="text-[11px] uppercase tracking-wide text-[#52796F] font-semibold mt-1.5">{label}</div>
    <div className="font-bold text-[#1C1C1C]">{pct?.toFixed(0)}%</div>
  </div>
);

const Moisture = ({ label, value }) => {
  // Convert m³/m³ to a friendlier 0-100 % scale (typical soil moisture range 0.05-0.45)
  const pct = value != null ? Math.round(Math.min(1, value / 0.5) * 100) : null;
  const color = pct == null ? "#52796F" : pct < 20 ? "#D90429" : pct < 35 ? "#E9C46A" : "#2B9348";
  return (
    <div className="flex items-center gap-3">
      <div className="relative w-12 h-12 shrink-0">
        <svg viewBox="0 0 36 36" className="w-12 h-12 -rotate-90">
          <circle cx="18" cy="18" r="14" fill="none" stroke="#E5E0D8" strokeWidth="3" />
          {pct != null && (
            <circle
              cx="18" cy="18" r="14" fill="none"
              stroke={color} strokeWidth="3"
              strokeDasharray={`${(pct / 100) * 88} 100`}
              strokeLinecap="round"
            />
          )}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-[11px] font-bold">
          {pct != null ? `${pct}%` : "—"}
        </div>
      </div>
      <div className="text-xs text-[#2D3A35] leading-snug">
        <div className="font-semibold">{label}</div>
        <div className="text-[#52796F]">{value != null ? `${value} m³/m³` : "—"}</div>
      </div>
    </div>
  );
};

const List = ({ icon, title, items, testid }) => (
  <div data-testid={testid} className="bg-white rounded-2xl border border-[#E5E0D8] p-4">
    <div className="flex items-center gap-1.5 font-bold text-[#1C1C1C] mb-2">
      {icon} {title}
    </div>
    <ul className="text-sm text-[#2D3A35] space-y-1.5 list-disc pl-5">
      {items.map((it, i) => <li key={i}>{it}</li>)}
    </ul>
  </div>
);

const phHint = (ph, t) => {
  if (ph == null) return "";
  if (ph < 6) return t("soil.ph.acidic");
  if (ph > 8) return t("soil.ph.alkaline");
  return t("soil.ph.neutral");
};
